// frontend/src/components/Statistics/Statistics.tsx
import React, { useEffect, useRef, useState } from "react";
import "./Statistics.css";

interface StatItem {
  value: number;
  suffix: string;
  label: string;
}

interface HomepageStatsResponse {
  ok?: boolean;
  result?: {
    familyDoctorsCount?: number;
    patientsCount?: number;
  };
}

const FALLBACK_STATS: StatItem[] = [
  { value: 0, suffix: "+", label: "Family Doctors" },
  { value: 0, suffix: "+", label: "Patients" },
  { value: 100, suffix: "%", label: "Free to Use" },
];

const Statistics: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [stats, setStats] = useState<StatItem[]>(FALLBACK_STATS);
  const [counts, setCounts] = useState<number[]>(FALLBACK_STATS.map(() => 0));

  useEffect(() => {
    let isMounted = true;

    const loadStats = async () => {
      try {
        const response = await fetch("/api/homepage-stats", {
          method: "GET",
          headers: {
            Accept: "application/json",
          },
        });

        if (!response.ok) return;

        const data: HomepageStatsResponse = await response.json();

        const familyDoctorsCount = Number(data?.result?.familyDoctorsCount ?? 0);
        const patientsCount = Number(data?.result?.patientsCount ?? 0);

        if (!isMounted) return;

        const nextStats: StatItem[] = [
          {
            value: Number.isFinite(familyDoctorsCount) ? familyDoctorsCount : 0,
            suffix: "+",
            label: "Family Doctors",
          },
          {
            value: Number.isFinite(patientsCount) ? patientsCount : 0,
            suffix: "+",
            label: "Patients",
          },
          { value: 100, suffix: "%", label: "Free to Use" },
        ];

        setStats(nextStats);
        setCounts(nextStats.map(() => 0));
      } catch {
        // Keep fallback stats silently
      }
    };

    loadStats();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    const duration = 2000;
    const frameRate = 60;
    const totalFrames = (duration / 1000) * frameRate;

    let frame = 0;
    const interval = setInterval(() => {
      frame++;
      const progress = frame / totalFrames;
      const easeOut = 1 - Math.pow(1 - progress, 3);

      setCounts(stats.map((stat) => Math.round(stat.value * easeOut)));

      if (frame >= totalFrames) {
        clearInterval(interval);
        setCounts(stats.map((stat) => stat.value));
      }
    }, 1000 / frameRate);

    return () => clearInterval(interval);
  }, [isVisible, stats]);

  return (
    <section
      ref={sectionRef}
      className="ocf-statistics"
      aria-label="Platform statistics"
    >
      <div className="ocf-statistics-inner">
        {stats.map((stat, index) => (
          <div key={stat.label} className="ocf-statistics-item">
            <div className="ocf-statistics-value">
              {counts[index].toLocaleString()}
              <span className="ocf-statistics-suffix">{stat.suffix}</span>
            </div>
            <div className="ocf-statistics-label">{stat.label}</div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default Statistics;